import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Icon, SplitChecksToggleBreakdown, Tag, ToggleableBreakdown } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import UserInfo from 'screens/MakePayment/Calculator/components/UserInfo/UserInfo';
import { ICalculatorRules, ITipCalculatorParticipant, ITipCalculatorResultParticipant } from 'types';
import { buildParticipantSplitCheckBreakdown, centsToDollarsDisplay } from 'utils';

export interface IParticipantListItemProps {
  dataCy?: string;
  participant: ITipCalculatorParticipant;
  calculatorRules: ICalculatorRules;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding-bottom: ${toRem(16)};
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
  margin-bottom: ${toRem(16)};
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: ${toRem(16)} ${toRem(16)} 0;
`;

const BottomRow = styled(motion.div)`
  display: flex;
  flex-flow: column;
  justify-content: stretch;
  padding: 0 ${toRem(16)};
  overflow: hidden;
`;

const Breakdowns = styled.div`
  display: flex;
  flex-flow: column;
`;

const DisbursementMethodWrapper = styled.div`
  --disbursement-method-wrapper-size: ${toRem(75)};

  display: flex;
  justify-content: flex-end;
  min-width: var(--disbursement-method-wrapper-size);
  width: var(--disbursement-method-wrapper-size);
  max-width: var(--disbursement-method-wrapper-size);
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(8)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;
`;

const ParticipantInfo = styled(UserInfo)`
  width: calc(100% - ${toRem(40)}) !important;
  max-width: calc(100% - ${toRem(40)});
`;

const ParticipantInfoWrapper = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: calc(100% - ${toRem(75)});
  margin-right: ${toRem(8)};

  @media (min-width: 1330px) {
    flex-grow: 1;
    max-width: ${toRem(400)};
  }
`;

const EmployeeLaborTipContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  align-items: center;
`;

const Label = styled.div`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.medium} !important;
  color: ${theme.color.neutral[500]};
  margin-bottom: ${toRem(4)};
`;

const StyledTag = styled(Tag)`
  white-space: nowrap;
  font-size: ${toRem(11)};
  background-color: ${theme.color.primary[50]};
`;

const StyledToggleableBreakdown = styled(ToggleableBreakdown)`
  &:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

const TotalsGrid = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
  margin: ${toRem(16)} 0 0;
  padding: 0 ${toRem(24)};

  &.dt-totals-grid {
    display: none;
  }

  p {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.medium};
  }

  @media (min-width: 1330px) {
    margin: 0;

    &.m-totals-grid {
      display: none;
    }

    &.dt-totals-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      min-width: ${toRem(280)};
      grid-gap: ${toRem(16)};
    }
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: column;

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
    white-space: nowrap;
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
  }

  & button {
    position: relative;
    top: ${toRem(-0.5)};
  }

  & button span {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }
`;

const ParticipantListItem: React.FC<IParticipantListItemProps> = ({ dataCy = 'participant-list-item', participant, calculatorRules }) => {
  const [open, setOpen] = useState(false);

  const onTriggerClick = () => {
    setOpen(!open);
  };

  const renderSharedBreakdown = useMemo(
    () =>
      participant.calculations.map((calculation: ITipCalculatorResultParticipant) => (
        <StyledToggleableBreakdown
          calculatorRules={calculatorRules.rules}
          categoryGroups={calculatorRules.categoryGroups}
          calculation={{ ...calculation, job: { title: participant.name }, totals: { ...calculation.totals, totalMinWorked: 0 } }}
          key={`${calculation.name}_${calculation.segmentId}`}
          segmentName={calculation.name}
          disabled
        />
      )),
    [participant]
  );

  const renderTotalsGrid = (className: string) => (
    <TotalsGrid className={className}>
      <TotalWrapper data-cy="calculated-tips-total">
        <Label>Calculated Tips</Label>
        <p>{centsToDollarsDisplay(participant.gross)}</p>
      </TotalWrapper>
    </TotalsGrid>
  );

  const renderOverrideBreakdown = useMemo(() => {
    const splitChecks = buildParticipantSplitCheckBreakdown(participant?.calculations);

    if (splitChecks?.received.length <= 0 && splitChecks?.contributions.length <= 0) return <></>;
    return <SplitChecksToggleBreakdown splitChecks={splitChecks} disabled />;
  }, [participant]);

  return (
    <Layout data-cy={dataCy} key={`participant-${participant.name}-${participant.type}`}>
      <TopRow className="top-row">
        <ParticipantInfoWrapper onClick={onTriggerClick}>
          <DropdownIconWrapper data-cy={`${dataCy}-accordion-trigger`}>
            <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
          </DropdownIconWrapper>
          <ParticipantInfo className="participant-info" hideAvatar username={participant?.name} dataCy={`${dataCy}-participant-info`} />
        </ParticipantInfoWrapper>
        <EmployeeLaborTipContainer>
          {renderTotalsGrid('dt-totals-grid')}
          <DisbursementMethodWrapper>
            <StyledTag appearance={Appearance.PRIMARY} label={participant.type} dataCy="participant-type" />
          </DisbursementMethodWrapper>
        </EmployeeLaborTipContainer>
      </TopRow>
      {renderTotalsGrid('m-totals-grid')}
      <BottomRow initial={open} animate={{ height: open ? 'auto' : 0, marginTop: open ? toRem(16) : 0 }} transition={theme.transition.spring.accordion} data-cy={`${dataCy}-bottom-row`}>
        <Breakdowns>
          {renderSharedBreakdown}
          {renderOverrideBreakdown}
        </Breakdowns>
      </BottomRow>
    </Layout>
  );
};

export default ParticipantListItem;
